import React from "react";
import { Badge, Box, Button, Card, Group, Text, Tooltip } from "@mantine/core";
import { IconCheck, IconChevronDown, IconShieldLock, IconListDetails, IconX } from "@tabler/icons-react";
import { assistantUiText } from "./assistantUiTranslations";
import { assistantDirection, translateAssistantLabel, translateAssistantText } from "./assistantLocalization";
import classes from "./assistantDrawer.module.css";

const fieldKey = (label) => String(label || "").replace(/[\s_.-]/g, "").toLowerCase();
const TITLE_FIELDS = new Set(["title", "subject", "name"]);
const DESCRIPTION_FIELDS = new Set(["desc", "description"]);
const TECHNICAL_FIELDS = new Set(["module", "taxonomyconfidence", "confidence", "permissionresult", "approvalid", "toolcallid", "rawresult"]);

// Keep every field available; only implementation details start collapsed.
export function groupActionFields(fields = []) {
	const result = { title: null, description: null, facts: [], details: [] };
	fields.forEach((field) => {
		if (!field || !field.label || field.value == null || field.value === "") return;
		const key = fieldKey(field.label);
		if (TITLE_FIELDS.has(key) && !result.title) result.title = field;
		else if (DESCRIPTION_FIELDS.has(key) && !result.description) result.description = field;
		else if (TECHNICAL_FIELDS.has(key) || ["_id", "id"].includes(String(field.label).toLowerCase())) result.details.push(field);
		else result.facts.push(field);
	});
	return result;
}

export default function AssistantActionCard({ block, lang, formatValue, onApprove, onReject, busy = false, resolvedApprovalIds = new Set() }) {
	const ui = (key, variables) => assistantUiText(key, lang, variables);
	const approval = block.type === "approval";
	const approvalId = block.approvalId ? String(block.approvalId) : "";
	const resolved = approvalId && resolvedApprovalIds.has(approvalId);
	const canReview = approval && approvalId && !resolved;
	const outcome = block.outcome || (block.type === "confirmation" ? block : null);
	const ticketRoute = outcome?.result?.route;
	const createdTicket = !outcome?.result?.draft && outcome?.result?.item?._id && /^\/ticket-system\/ticket\/[a-f0-9]{24}$/i.test(ticketRoute || "");
	const draftReady = outcome?.summary === "Draft prepared. No database write was performed.";
	const failed = block.type === "failure";
	const fields = groupActionFields(approval ? block.fields : block.items);
	const title = translateAssistantLabel(block.title || (outcome ? "done" : "details"), lang);
	const summary = draftReady ? ui("draftNotice") : translateAssistantText(outcome?.summary || block.summary, lang);
	const Icon = failed ? IconX : approval ? IconShieldLock : outcome ? IconCheck : IconListDetails;
	const fieldValue = (field) => formatValue(field.value, lang, field.label);
	const statusTitle = draftReady ? ui("draftReady") : outcome ? translateAssistantLabel(outcome.title || "done", lang) : null;

	return (
		<Card withBorder radius="lg" p="sm" className={classes.actionCard} dir={assistantDirection(lang)} data-failed={failed || undefined}>
			<Box className={classes.actionHeader}>
				<Box className={classes.actionHeading}>
					<Box className={classes.actionIcon} data-approval={approval || undefined}><Icon size={17} /></Box>
					<Box className={classes.actionHeadingText}>
						<Text size="sm" weight={700}>{draftReady && !block.outcome ? statusTitle : title}</Text>
						{statusTitle && block.outcome && <Text size="xs" color="teal" weight={600}>{statusTitle}</Text>}
					</Box>
				</Box>
				{approval && <Group spacing={6}>
					<Tooltip label={ui("risk")}><Badge size="xs" variant="light" color={block.riskLevel === "high" ? "red" : "orange"}>
						{ui("risk")}: {translateAssistantLabel(block.riskLevel || "medium", lang)}
					</Badge></Tooltip>
					{!canReview && <Badge size="xs" color="gray" variant="light">{ui(resolved ? "reviewCompleted" : "approvalUnavailable")}</Badge>}
				</Group>}
			</Box>
			{summary && <Text size="xs" color="dimmed" className={classes.actionSummary}>{summary}</Text>}
			{(fields.title || fields.description) && <Box className={classes.actionLead}>
				{fields.title && <Text size="md" weight={600} dir="auto">{fieldValue(fields.title)}</Text>}
				{fields.description && <Text size="sm" color="dimmed" dir="auto" className={classes.actionDescription}>{fieldValue(fields.description)}</Text>}
			</Box>}
			{fields.facts.length > 0 && <Box className={classes.actionFacts}>
				{fields.facts.map((field, index) => <Box className={classes.actionFact} key={`${field.label}-${index}`}>
					<Text size="xs" color="dimmed">{translateAssistantLabel(field.label, lang)}</Text>
					<Text size="xs" weight={600} dir="auto">{fieldValue(field)}</Text>
				</Box>)}
			</Box>}
			{fields.details.length > 0 && <details className={classes.actionDetails}>
				<summary><IconChevronDown size={14} />{ui("actionDetails", { count: fields.details.length })}</summary>
				<Box className={classes.actionFacts}>
					{fields.details.map((field, index) => <Box className={classes.actionFact} key={`${field.label}-${index}`}>
						<Text size="xs" color="dimmed">{translateAssistantLabel(field.label, lang)}</Text>
						<Text size="xs" dir="auto">{fieldValue(field)}</Text>
					</Box>)}
				</Box>
			</details>}
			{createdTicket && <Group position="right" className={classes.actionFooter}><Button component="a" href={ticketRoute} size="xs" radius="xl" variant="light">{ui("openTicket")}</Button></Group>}
			{canReview && <Group position="right" spacing={8} className={classes.actionFooter}>
				<Button size="xs" radius="xl" variant="subtle" color="red" leftIcon={<IconX size={14} />} disabled={busy} onClick={() => onReject(block.approvalId)}>{ui("rejectLabel")}</Button>
				<Button size="xs" radius="xl" color="green" leftIcon={<IconCheck size={14} />} loading={busy} disabled={busy} onClick={() => onApprove(block.approvalId)}>{ui("approveLabel")}</Button>
			</Group>}
		</Card>
	);
}
